/*****************************************************************
 *  CUSTOMER BUTLER - THE PAGE DOORS (which look to show)          *
 *  File: apps/web/src/pages/customer/CustomerPages.js             *
 *  What it does: the four pages App.js mounts:                    *
 *    /customers        CustomersEntry                             *
 *    /customers/<id>   CustomerEntry                              *
 *    /properties       PropertiesEntry                            *
 *    /properties/<id>  PropertyEntry                              *
 *  Three looks (2026-10-15): the HR Butler look (default), the    *
 *  clean Zoho-arranged look of 2026-10-14, and the classic pages  *
 *  (untouched originals, one line under them to come back).       *
 *  The property pages have the HR and the clean look.             *
 *  Reads/Writes: the per-device look choice (customerLook.js).    *
 *****************************************************************/

/* WHY (2026-10-14, James): "skins are only ever added, never removed". The
   classic CustomersPage / CustomerDetailPage files keep their look.

   WHY (2026-10-14, later, James's Honest UI rule via Fable): every page here
   now stands behind CustomerAccessGate. It asks the server once what this
   person's Customer Butler level is (set on the HR Team page). No access =
   a plain page saying so; otherwise both looks draw only the buttons that
   level allows (the classic pages got the same few checks).

   WHY (2026-10-15, 01:04 ET, James): "what you made vs the design spec" -
   HR Butler is the spec. A third look, the HR Butler look, is ADDED and is
   the default; the clean look of 2026-10-14 and the classic pages both stay
   one tap away at the foot of the page.

   WHY (2026-10-15, later, James via Fable): the HR look stays; the RECORD
   pages in it take Zoho's record-page structure (ZohoRecordPages.js). The
   folded-card record (HrRecordPages.js) is kept in the code, not drawn. */

import React from 'react';
import CustomersPage from '../CustomersPage';
import CustomerDetailPage from '../CustomerDetailPage';
import { useCustomerSkin, SKIN_CLASSIC, SKIN_HR } from './customerLook';
import { HrCustomerListPage, HrPropertyListPage } from './HrListPages';
import { ZohoCustomerRecordPage, ZohoPropertyRecordPage } from './ZohoRecordPages';
import { HrSetupFrame } from './HrChrome';
import { CustomerListPage } from './CustomerListPage';
import { CustomerRecordPage } from './CustomerRecordPage';
import { PropertyListPage } from './PropertyListPage';
import { PropertyRecordPage } from './PropertyRecordPage';
import { CustomerAccessGate } from './CustomerAccessGate';
import './customer.css';

/* ---- BackToNewLookLine({ onChooseSkin }) -----------------------
   The one line under a classic page that switches back to the
   default look (the HR Butler look since 2026-10-15).           */
function BackToNewLookLine({ onChooseSkin }) {
  return (
    <p className="cb-look">
      <button type="button" className="cb-link-btn" onClick={() => onChooseSkin(SKIN_HR)}>
        Switch to the HR Butler look
      </button>
    </p>
  );
}

/* ---- CustomersListInChosenLook() -- the customer list in the chosen look ---- */
function CustomersListInChosenLook() {
  const [skinName, chooseSkin] = useCustomerSkin();

  if (skinName === SKIN_CLASSIC) {
    return (
      <>
        <CustomersPage />
        <BackToNewLookLine onChooseSkin={chooseSkin} />
      </>
    );
  }

  if (skinName === SKIN_HR) {
    return <HrCustomerListPage onChooseSkin={chooseSkin} />;
  }

  return <CustomerListPage onChooseSkin={chooseSkin} />;
}

/* ---- OneCustomerInChosenLook() -- one customer in the chosen look ---- */
function OneCustomerInChosenLook() {
  const [skinName, chooseSkin] = useCustomerSkin();

  if (skinName === SKIN_CLASSIC) {
    return (
      <>
        <CustomerDetailPage />
        <BackToNewLookLine onChooseSkin={chooseSkin} />
      </>
    );
  }

  if (skinName === SKIN_HR) {
    return <ZohoCustomerRecordPage onChooseSkin={chooseSkin} />;
  }

  return <CustomerRecordPage onChooseSkin={chooseSkin} />;
}

/* ---- PropertiesListInChosenLook() --------------------------------
   The classic look never had property pages, so classic shows the
   clean property list (as it did on 2026-10-14).                */
function PropertiesListInChosenLook() {
  const [skinName, chooseSkin] = useCustomerSkin();

  if (skinName === SKIN_HR) {
    return <HrPropertyListPage onChooseSkin={chooseSkin} />;
  }

  return <PropertyListPage />;
}

/* ---- OnePropertyInChosenLook() -- one property in the chosen look ---- */
function OnePropertyInChosenLook() {
  const [skinName, chooseSkin] = useCustomerSkin();

  if (skinName === SKIN_HR) {
    return <ZohoPropertyRecordPage onChooseSkin={chooseSkin} />;
  }

  return <PropertyRecordPage />;
}

/*****************************************************************
 *  THE FOUR DOORS APP.JS MOUNTS - each behind the access gate     *
 *****************************************************************/

/* ---- CustomersEntry() -- /customers ---- */
export function CustomersEntry() {
  return (
    <CustomerAccessGate needs="open">
      <CustomersListInChosenLook />
    </CustomerAccessGate>
  );
}

/* ---- CustomerEntry() -- /customers/<id> ---- */
export function CustomerEntry() {
  return (
    <CustomerAccessGate needs="open">
      <OneCustomerInChosenLook />
    </CustomerAccessGate>
  );
}

/* ---- PropertiesEntry() -- /properties (HR or clean) ---- */
export function PropertiesEntry() {
  return (
    <CustomerAccessGate needs="open">
      <PropertiesListInChosenLook />
    </CustomerAccessGate>
  );
}

/* ---- PropertyEntry() -- /properties/<id> (HR or clean) ---- */
export function PropertyEntry() {
  return (
    <CustomerAccessGate needs="open">
      <OnePropertyInChosenLook />
    </CustomerAccessGate>
  );
}

/* ---- SetupInChosenLook({ children }) -----------------------------
   CRM Setup inside the HR frame for the HR look; as it was for the
   clean and classic looks.                                      */
function SetupInChosenLook({ children }) {
  const [skinName, chooseSkin] = useCustomerSkin();

  if (skinName === SKIN_HR) {
    return <HrSetupFrame onChooseSkin={chooseSkin}>{children}</HrSetupFrame>;
  }

  return children;
}

/* ---- CrmSetupEntry({ children }) -- /crm-setup, Manager only ---- */
export function CrmSetupEntry({ children }) {
  return (
    <CustomerAccessGate needs="setup">
      <SetupInChosenLook>{children}</SetupInChosenLook>
    </CustomerAccessGate>
  );
}
